export type DebouncedFunction<Args extends unknown[], ResultType> = ((
  ...args: Args
) => Promise<ResultType>) & {
  cancel: () => void;
};

export class DebounceCancelledError extends Error {
  constructor() {
    super('Debounced call cancelled');
    this.name = 'DebounceCancelledError';
  }
}

export const isDebounceCancelledError = (error: unknown): boolean =>
  error instanceof DebounceCancelledError;

export default function debounce<Args extends unknown[], ResultType>(
  debouncedFunction: (...args: Args) => Promise<ResultType>,
  delay: number,
): DebouncedFunction<Args, ResultType> {
  let timeoutID: number | undefined;
  let lastCallTime: number | undefined;
  const promises: Array<{
    resolve: (value: ResultType | PromiseLike<ResultType>) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  const cancel = () => {
    if (timeoutID !== undefined) {
      clearTimeout(timeoutID);
      timeoutID = undefined;
    }

    lastCallTime = undefined;

    const pending = promises.splice(0, promises.length);

    pending.forEach((promiseActions) => promiseActions.reject(new DebounceCancelledError()));
  };

  const debounced = ((...args: Args): Promise<ResultType> => {
    const now = Date.now();

    if (lastCallTime && now - lastCallTime < delay) {
      if (timeoutID) {
        clearTimeout(timeoutID);
      }
    } else {
      lastCallTime = now;
    }

    const myPromise: Promise<ResultType> = new Promise((resolve, reject) => {
      promises.push({ resolve, reject });
    });

    timeoutID = window.setTimeout(() => {
      timeoutID = undefined;
      lastCallTime = undefined;

      const pending = promises.splice(0, promises.length);

      debouncedFunction(...args)
        .then((data) => {
          pending.forEach((promiseActions) => promiseActions.resolve(data));
        })
        .catch((e) => {
          pending.forEach((promiseActions) => promiseActions.reject(e));
        });
    }, delay);

    return myPromise;
  }) as DebouncedFunction<Args, ResultType>;

  debounced.cancel = cancel;

  return debounced;
}
